<script setup lang="ts">
import { computed, nextTick, onBeforeUnmount, ref, useId, watch } from 'vue'
import { CloseIcon } from '@solar-icons/vue/linear'
import AppButton from '../../primitives/AppButton/AppButton.vue'
import {
  useAnchoredPosition,
  type AnchorPlacement,
} from '../../primitives/AppPopover/use-anchored-position'
import { useFocusTrap } from '../../../composables/useFocusTrap'
import { useReturnFocus } from '../../../composables/useReturnFocus'
import type { CoachmarkRect, CoachmarkVariant } from './coachmark-types'

const props = withDefaults(
  defineProps<{
    /** The element being explained. Nothing renders while this is `null`. */
    target: HTMLElement | null
    /** Side of the target the card sits on; flips itself near a viewport edge. */
    placement?: AnchorPlacement
    /** Card heading. Copy belongs to the tour registry, never to this component. */
    title: string
    body: string
    /** 0-based index of this step, for the progress dots. */
    stepIndex?: number
    /** Total steps in the tour. Dots and `stepLabel` render only when both counts are present. */
    stepCount?: number
    stepLabel?: string
    /** `soft` (default) rings the target and leaves the page usable; `spotlight` frosts it and traps focus. */
    variant?: CoachmarkVariant
    /** Advance button label. The button renders only when this is set. */
    nextLabel?: string
    /** Back button label. The button renders only when this is set. */
    backLabel?: string
    /** Skip / dismiss-the-tour button label. The button renders only when this is set. */
    skipLabel?: string
    closeLabel: string
  }>(),
  {
    placement: 'right-start',
    stepIndex: undefined,
    stepCount: undefined,
    stepLabel: undefined,
    variant: 'soft',
    nextLabel: undefined,
    backLabel: undefined,
    skipLabel: undefined,
  },
)

const emit = defineEmits<{
  next: []
  back: []
  skip: []
  close: []
}>()

const CARD_OFFSET = 12
const VIEWPORT_PADDING = 8
const CUTOUT_PADDING = 4

const titleId = useId()
const bodyId = useId()

const cardRef = ref<HTMLElement | null>(null)
const targetRef = computed(() => props.target)

const trap = useFocusTrap(cardRef)
const returnFocus = useReturnFocus()

const { floatingStyles, start, stop, update } = useAnchoredPosition(targetRef, cardRef, () => ({
  placement: props.placement,
  offset: CARD_OFFSET,
  padding: VIEWPORT_PADDING,
}))

const targetRect = ref<CoachmarkRect | null>(null)
const viewport = ref({ width: 0, height: 0 })

let rafId = 0
let resizeObserver: ResizeObserver | undefined

function measure(): void {
  const el = props.target
  if (!el || typeof window === 'undefined') {
    targetRect.value = null
    return
  }
  const rect = el.getBoundingClientRect()
  targetRect.value = {
    top: rect.top,
    left: rect.left,
    width: rect.width,
    height: rect.height,
  }
  viewport.value = {
    width: document.documentElement.clientWidth,
    height: document.documentElement.clientHeight,
  }
}

function scheduleMeasure(): void {
  if (typeof window === 'undefined') return
  cancelAnimationFrame(rafId)
  rafId = requestAnimationFrame(measure)
}

function startTracking(): void {
  if (typeof window === 'undefined') return
  measure()
  window.addEventListener('scroll', scheduleMeasure, { capture: true, passive: true })
  window.addEventListener('resize', scheduleMeasure, { passive: true })
  if (props.target && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(scheduleMeasure)
    resizeObserver.observe(props.target)
  }
}

function stopTracking(): void {
  if (typeof window === 'undefined') return
  cancelAnimationFrame(rafId)
  window.removeEventListener('scroll', scheduleMeasure, { capture: true })
  window.removeEventListener('resize', scheduleMeasure)
  resizeObserver?.disconnect()
  resizeObserver = undefined
}

function attach(): void {
  startTracking()
  start()
  if (props.variant === 'spotlight') {
    returnFocus.capture()
    trap.activate()
  }
  cardRef.value?.focus({ preventScroll: true })
}

function detach(): void {
  stopTracking()
  stop()
  if (props.variant === 'spotlight') {
    trap.deactivate()
    returnFocus.restore()
  }
}

const travelling = ref(false)
let travelTimer: ReturnType<typeof setTimeout> | undefined

const TRAVEL_MS = 360

function travel(): void {
  travelling.value = true
  clearTimeout(travelTimer)
  travelTimer = setTimeout(() => {
    travelling.value = false
  }, TRAVEL_MS)
}

const travelClass = computed(() =>
  travelling.value
    ? 'transition-[top,left,width,height] duration-slow ease-soft motion-reduce:transition-none'
    : '',
)

watch(
  () => props.target,
  async (target, previous) => {
    if (previous) detach()
    if (!target) return
    if (previous) travel()
    await nextTick()
    attach()
  },
  { immediate: true, flush: 'post' },
)

watch(
  () => [props.placement, props.title, props.body] as const,
  async () => {
    if (!props.target) return
    await nextTick()
    measure()
    update()
  },
)

onBeforeUnmount(detach)

const cutout = computed(() => {
  const rect = targetRect.value
  if (!rect) return null
  return {
    top: rect.top - CUTOUT_PADDING,
    left: rect.left - CUTOUT_PADDING,
    width: rect.width + CUTOUT_PADDING * 2,
    height: rect.height + CUTOUT_PADDING * 2,
  }
})

function px(value: number): string {
  return `${Math.round(Math.max(0, value))}px`
}

const ringStyle = computed(() => {
  const rect = targetRect.value
  if (!rect) return undefined
  return {
    top: px(rect.top),
    left: px(rect.left),
    width: px(rect.width),
    height: px(rect.height),
  }
})

const frostPanels = computed(() => {
  const box = cutout.value
  if (!box) return []
  const { width: vw, height: vh } = viewport.value
  const bottom = box.top + box.height
  const right = box.left + box.width
  return [
    { key: 'top', top: px(0), left: px(0), width: px(vw), height: px(box.top) },
    { key: 'bottom', top: px(bottom), left: px(0), width: px(vw), height: px(vh - bottom) },
    { key: 'left', top: px(box.top), left: px(0), width: px(box.left), height: px(box.height) },
    {
      key: 'right',
      top: px(box.top),
      left: px(right),
      width: px(vw - right),
      height: px(box.height),
    },
  ]
})

const dots = computed(() => {
  const count = props.stepCount
  if (count === undefined || props.stepIndex === undefined) return []
  return Array.from({ length: count }, (_, index) => index)
})

const showProgress = computed(() => dots.value.length > 0)

const INTERACTIVE = 'a, button, input, select, textarea, [contenteditable]'

function onKeydown(event: KeyboardEvent): void {
  if (event.key === 'Escape') {
    event.preventDefault()
    emit('skip')
    return
  }

  const onControl = (event.target as HTMLElement | null)?.closest(INTERACTIVE) != null

  if ((event.key === 'ArrowRight' || (event.key === 'Enter' && !onControl)) && props.nextLabel) {
    event.preventDefault()
    emit('next')
    return
  }

  if (event.key === 'ArrowLeft' && props.backLabel) {
    event.preventDefault()
    emit('back')
  }
}
</script>

<template>
  <template v-if="target">
    <template v-if="variant === 'spotlight'">
      <div
        v-for="panel in frostPanels"
        :key="panel.key"
        aria-hidden="true"
        :class="[
          'scrim-fallback fixed z-40 bg-scrim backdrop-blur-scrim animate-fade-in',
          travelClass,
        ]"
        :style="{ top: panel.top, left: panel.left, width: panel.width, height: panel.height }"
      />
    </template>

    <div
      v-if="ringStyle"
      aria-hidden="true"
      :class="[
        'pointer-events-none fixed z-50 rounded-4 outline-2 outline-offset-2 outline-accent-blue',
        'animate-fade-in',
        travelClass,
      ]"
      :style="ringStyle"
    />

    <div
      ref="cardRef"
      role="dialog"
      :aria-labelledby="titleId"
      :aria-describedby="bodyId"
      tabindex="-1"
      :style="floatingStyles"
      :class="[
        'z-50 flex w-(--container-coachmark) flex-col gap-8 rounded-4 border border-border',
        'bg-bg-primary p-12 shadow-3 outline-none animate-zoom-in',
        travelClass,
      ]"
      @keydown="onKeydown"
    >
      <div class="flex items-start gap-8">
        <p v-if="stepLabel && showProgress" class="text-caption text-text-tertiary">
          {{ stepLabel }}
        </p>
        <AppButton
          size="sm"
          icon-only
          variant="ghost"
          class="ml-auto"
          :start-icon="CloseIcon"
          :aria-label="closeLabel"
          @click="emit('close')"
        />
      </div>

      <div :key="title" class="flex flex-col gap-4 animate-fade-in">
        <h2 :id="titleId" class="text-body-emphasized text-text-primary">{{ title }}</h2>
        <p :id="bodyId" class="text-body-sm text-text-secondary">{{ body }}</p>
      </div>

      <div class="flex items-center gap-8">
        <div v-if="showProgress" class="flex items-center gap-2">
          <span
            v-for="dot in dots"
            :key="dot"
            :class="[
              'size-2 rounded-full',
              dot === stepIndex ? 'bg-accent-blue' : 'bg-bg-tertiary',
            ]"
          />
        </div>

        <div class="ml-auto flex items-center gap-4">
          <AppButton
            v-if="skipLabel"
            variant="ghost"
            class="whitespace-nowrap"
            :label="skipLabel"
            @click="emit('skip')"
          />
          <AppButton
            v-if="backLabel"
            variant="secondary"
            class="whitespace-nowrap"
            :label="backLabel"
            @click="emit('back')"
          />
          <AppButton
            v-if="nextLabel"
            variant="primary"
            class="whitespace-nowrap"
            :label="nextLabel"
            @click="emit('next')"
          />
        </div>
      </div>
    </div>
  </template>
</template>
